import { Button, Checkbox, Circle, Fieldset, Float, Menu, Portal } from '@chakra-ui/react';
import { UseNavigateResult } from '@tanstack/react-router';
import { t } from 'i18next';
import { MdViewColumn } from 'react-icons/md';

import { Tooltip } from '@/components/ui/tooltip';
import useMediaQuery from '@/hooks/use-media-query';

export const TableColumnVisibilityMenu = ({
  columns,
  columnsDef,
  defaultColumnVisibility,
  navigate,
}: {
  columns: string;
  columnsDef: Array<Record<string, any>>;
  defaultColumnVisibility: Record<string, boolean>;
  navigate: UseNavigateResult<''>;
}) => {
  const defaultColumns: string[] = Object.keys(defaultColumnVisibility).filter(
    (key) => defaultColumnVisibility[key as keyof typeof defaultColumnVisibility]
  );
  const urlColumns = columns ? columns.split(',') : defaultColumns;

  const arraysEqual = (arr1: string[], arr2: string[]) => {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((value, index) => value === arr2[index]);
  };

  const handleChange = (selected: string[]) => {
    if (arraysEqual(selected, defaultColumns)) {
      navigate({
        search: (prev: Record<string, string>) => ({
          ...prev,
          columns: undefined,
        }),
      });
    } else {
      navigate({
        search: (prev: Record<string, string>) => ({
          ...prev,
          columns: selected.join(','),
        }),
      });
    }
  };

  const handleIndividualChange = (accessorKey: string, checked: boolean) => {
    let newColumns: string[];
    if (checked) {
      newColumns = [...urlColumns, accessorKey];
    } else {
      newColumns = urlColumns.filter((col) => col !== accessorKey);
    }

    handleChange(newColumns);
  };
  const isTooltipHidden = useMediaQuery('(min-width: 1250px)');

  return (
    <Menu.Root closeOnSelect={false}>
      <Menu.Trigger as="div">
        <Tooltip
          showArrow
          content={t('ui.columns')}
          openDelay={100}
          closeDelay={100}
          contentProps={{ css: { '--tooltip-bg': 'colors.primary' } }}
          disabled={isTooltipHidden}
        >
          <Button variant="outline" size="sm" {...(!isTooltipHidden && { p: '0' })}>
            <MdViewColumn />
            {isTooltipHidden && <span>{t('ui.columns')}</span>}
            <Float offsetY="1">
              <Circle size="5" bg="primary" color="white" fontSize={'xs'}>
                {urlColumns.length}
              </Circle>
            </Float>
          </Button>
        </Tooltip>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content>
            <Fieldset.Root>
              <Fieldset.Content p={3}>
                {columnsDef.map(({ header, accessorKey, options }) => (
                  <Checkbox.Root
                    key={accessorKey}
                    checked={urlColumns.includes(accessorKey)}
                    onCheckedChange={(details) => {
                      handleIndividualChange(accessorKey, !!details.checked);
                    }}
                    disabled={options?.enableHiding === false}
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control bg="white" _checked={{ bg: 'primary.500', border: 'none' }} />
                    <Checkbox.Label
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {typeof header === 'function' ? header() : header}
                    </Checkbox.Label>
                  </Checkbox.Root>
                ))}
              </Fieldset.Content>
            </Fieldset.Root>
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
};
